import { useEffect, useState } from 'react';
import { listarApuracoesPisCofins, apurarPisCofins, aprovarPisCofins, abrirDocumentoAutenticado } from '../api';
import { PageHeader, Loading, Empty, StatusBadge, Alert, KPI } from '../components/common';
import { fmtMoeda, fmtData, mesAtual, anosOpcoes } from '../lib/format';

export default function ApuracaoPisCofins({ clienteId }) {
  const comp = mesAtual();
  const [ano, mes] = comp.split('-').map(Number);

  const [filtroAno, setFiltroAno] = useState(ano);
  const [apuracoes, setApuracoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensagem, setMensagem] = useState(null);
  const [formAberto, setFormAberto] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  // Form
  const [formComp, setFormComp] = useState(`${ano}-${String(mes).padStart(2, '0')}`);
  const [formRegime, setFormRegime] = useState('cumulativo');
  const [formReceita, setFormReceita] = useState('');
  const [formExclusoes, setFormExclusoes] = useState('');
  const [formCreditosPis, setFormCreditosPis] = useState('');
  const [formCreditosCofins, setFormCreditosCofins] = useState('');
  const [formRetencoesPis, setFormRetencoesPis] = useState('');
  const [formRetencoesCofins, setFormRetencoesCofins] = useState('');
  const [apurando, setApurando] = useState(false);

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    listarApuracoesPisCofins(clienteId, { ano: filtroAno })
      .then(r => setApuracoes(r.items || []))
      .catch(err => setMensagem({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteId, filtroAno]);

  const apurar = async () => {
    setApurando(true);
    setMensagem(null);
    try {
      const dados = {
        competencia: formComp,
        regime: formRegime,
        exclusoes:        Number(formExclusoes || 0),
        creditos_pis:     Number(formCreditosPis || 0),
        creditos_cofins:  Number(formCreditosCofins || 0),
        retencoes_pis:    Number(formRetencoesPis || 0),
        retencoes_cofins: Number(formRetencoesCofins || 0),
      };
      if (formReceita) dados.receita_bruta = Number(formReceita);
      const r = await apurarPisCofins(clienteId, dados);
      const a = r.apuracao;
      setMensagem({
        tipo: 'success',
        txt: `${formComp} apurado · PIS R$ ${a.pis_a_pagar} + COFINS R$ ${a.cofins_a_pagar}`
          + (r.receita ? ` · Receita ${fmtMoeda(r.receita.valor)} (${r.receita.origem})` : ''),
      });
      setFormReceita(''); setFormExclusoes('');
      setFormCreditosPis(''); setFormCreditosCofins('');
      setFormRetencoesPis(''); setFormRetencoesCofins('');
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    } finally {
      setApurando(false);
    }
  };

  const aprovar = async (id) => {
    try {
      await aprovarPisCofins(id);
      setMensagem({ tipo: 'success', txt: `Apuração #${id} aprovada` });
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    }
  };

  if (!clienteId) return <Empty title="Selecione um cliente" />;

  // KPIs
  const totalPisAno    = apuracoes.reduce((s, a) => s + Number(a.pis_a_pagar    || 0), 0);
  const totalCofinsAno = apuracoes.reduce((s, a) => s + Number(a.cofins_a_pagar || 0), 0);
  const ultima = apuracoes[0];

  // Memoria de calculo imprimivel: rota autenticada, aba aberta no gesto.
  const imprimir = (a) =>
    abrirDocumentoAutenticado(
      `/apuracao-pis-cofins/${clienteId}/${a.competencia}/html`, 'apuracao de PIS/COFINS')
      .catch(err => setMensagem({ tipo: 'danger', txt: 'Não foi possível gerar: ' + err.message }));

  return (
    <>
      <PageHeader
        title="Apuração PIS/COFINS"
        sub="Lucro Presumido (cumulativo) ou Lucro Real (não-cumulativo)"
        actions={
          <button className="primary" onClick={() => setFormAberto(v => !v)}>
            {formAberto ? '▴ Ocultar formulário' : '+ Nova apuração'}
          </button>
        }
      />

      {mensagem && <Alert tipo={mensagem.tipo}>{mensagem.txt}</Alert>}

      <div className="kpi-grid">
        <KPI label={`PIS total ${filtroAno}`} value={fmtMoeda(totalPisAno)} />
        <KPI label={`COFINS total ${filtroAno}`} value={fmtMoeda(totalCofinsAno)} />
        <KPI label={`Apurações em ${filtroAno}`} value={apuracoes.length} />
        {ultima && (
          <KPI
            label={`Última (${ultima.competencia})`}
            value={`${ultima.regime === 'cumulativo' ? 'Cum.' : 'Não-cum.'}`}
            sub={`Base R$ ${Number(ultima.base_calculo).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          />
        )}
      </div>

      {formAberto && (
        <div className="card" style={{ padding: 14, marginBottom: 16, borderLeft: '3px solid var(--gold)' }}>
          <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
            Nova apuração mensal
          </h3>
          <div className="flex" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div>
              <label className="small muted">Competência</label>
              <input type="month" value={formComp} onChange={e => setFormComp(e.target.value)} style={{ width: 140 }} />
            </div>
            <div>
              <label className="small muted">Regime</label>
              <select value={formRegime} onChange={e => setFormRegime(e.target.value)} style={{ width: 200 }}>
                <option value="cumulativo">Cumulativo (LP) — 0,65% + 3,00%</option>
                <option value="nao_cumulativo">Não-cumulativo (LR) — 1,65% + 7,60%</option>
              </select>
            </div>
            <div>
              <label className="small muted">Receita bruta (vazio = automático)</label>
              <input type="number" step="0.01" min="0" value={formReceita} onChange={e => setFormReceita(e.target.value)} placeholder="auto" style={{ width: 180, textAlign: 'right' }} />
            </div>
            <div>
              <label className="small muted">Exclusões (vendas canc., desc., IPI)</label>
              <input type="number" step="0.01" min="0" value={formExclusoes} onChange={e => setFormExclusoes(e.target.value)} style={{ width: 160, textAlign: 'right' }} placeholder="0,00" />
            </div>
          </div>

          {formRegime === 'nao_cumulativo' && (
            <div className="flex" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
              <div>
                <label className="small muted">Créditos PIS</label>
                <input type="number" step="0.01" min="0" value={formCreditosPis} onChange={e => setFormCreditosPis(e.target.value)} style={{ width: 160, textAlign: 'right' }} />
              </div>
              <div>
                <label className="small muted">Créditos COFINS</label>
                <input type="number" step="0.01" min="0" value={formCreditosCofins} onChange={e => setFormCreditosCofins(e.target.value)} style={{ width: 160, textAlign: 'right' }} />
              </div>
            </div>
          )}

          <div className="flex" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label className="small muted">Retenções PIS (R-4020)</label>
              <input type="number" step="0.01" min="0" value={formRetencoesPis} onChange={e => setFormRetencoesPis(e.target.value)} style={{ width: 160, textAlign: 'right' }} />
            </div>
            <div>
              <label className="small muted">Retenções COFINS (R-4020)</label>
              <input type="number" step="0.01" min="0" value={formRetencoesCofins} onChange={e => setFormRetencoesCofins(e.target.value)} style={{ width: 160, textAlign: 'right' }} />
            </div>
            <button className="primary" onClick={apurar} disabled={apurando}>
              {apurando ? 'Apurando…' : 'Apurar'}
            </button>
          </div>

          <p className="small muted" style={{ marginTop: 10 }}>
            Receita bruta é resolvida automaticamente (escriturado → NFS-e); preencha manualmente apenas como último recurso. Saldo credor de meses anteriores é abatido automaticamente. Se retenção {'>'} apurado, gera saldo credor pro próximo mês (IN RFB 1.234/12).
          </p>
        </div>
      )}

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <select value={filtroAno} onChange={e => setFiltroAno(Number(e.target.value))}>
          {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading /> : apuracoes.length === 0 ? (
          <Empty title={`Sem apurações em ${filtroAno}`} />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Competência</th>
                <th>Regime</th>
                <th className="num">Base</th>
                <th className="num">PIS apurado</th>
                <th className="num">PIS retido</th>
                <th className="num">PIS a pagar</th>
                <th className="num">COFINS apurado</th>
                <th className="num">COFINS retido</th>
                <th className="num">COFINS a pagar</th>
                <th>Vencto</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {apuracoes.map(a => (
                <tr key={a.id}>
                  <td className="bold">{a.competencia}</td>
                  <td>
                    <span className="badge badge--info">
                      {a.regime === 'cumulativo' ? 'Cum.' : 'Não-cum.'}
                    </span>
                  </td>
                  <td className="num">{fmtMoeda(a.base_calculo)}</td>
                  <td className="num">{fmtMoeda(a.pis_apurado)}</td>
                  <td className="num" style={{ color: 'var(--text-2)' }}>{fmtMoeda(a.pis_retencao)}</td>
                  <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(a.pis_a_pagar)}</td>
                  <td className="num">{fmtMoeda(a.cofins_apurado)}</td>
                  <td className="num" style={{ color: 'var(--text-2)' }}>{fmtMoeda(a.cofins_retencao)}</td>
                  <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(a.cofins_a_pagar)}</td>
                  <td className="small">{fmtData(a.darf_data_vencto)}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td className="flex">
                    <button className="ghost" onClick={() => setDetalhe(a)}>Ver</button>
                    <button className="ghost" onClick={() => imprimir(a)}
                      title="Memoria de calculo em A4, com assinaturas">&#128424;</button>
                    {a.status === 'rascunho' && (
                      <button className="primary" onClick={() => aprovar(a.id)}>Aprovar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detalhe && <DetalhePisCofins apuracao={detalhe} onClose={() => setDetalhe(null)} />}
    </>
  );
}

function DetalhePisCofins({ apuracao, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onClose}>
      <div className="card" style={{ width: 640, maxHeight: '85vh', overflow: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, color: 'var(--gold)' }}>PIS/COFINS {apuracao.competencia}</h2>
          <div style={{ flex: 1 }} />
          <button onClick={onClose}>Fechar</button>
        </div>

        <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 16, marginBottom: 8 }}>
          Base de cálculo
        </h3>
        <table className="data" style={{ fontSize: 12 }}>
          <tbody>
            <tr>
              <td>Receita bruta{apuracao.receita_origem ? ` · origem ${apuracao.receita_origem}` : ''}</td>
              <td className="num bold">{fmtMoeda(apuracao.receita_bruta)}</td>
            </tr>
            <tr><td>Exclusões</td><td className="num">({fmtMoeda(apuracao.exclusoes)})</td></tr>
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold">Base de cálculo</td>
              <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(apuracao.base_calculo)}</td>
            </tr>
            <tr><td>Regime</td><td>{apuracao.regime === 'cumulativo' ? 'Cumulativo (LP)' : 'Não-cumulativo (LR)'}</td></tr>
          </tbody>
        </table>

        <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 16, marginBottom: 8 }}>
          PIS (DARF {apuracao.pis_darf_codigo})
        </h3>
        <table className="data" style={{ fontSize: 12 }}>
          <tbody>
            <tr><td>Alíquota</td><td className="num">{(Number(apuracao.pis_aliquota) * 100).toFixed(2)}%</td></tr>
            {apuracao.regime === 'nao_cumulativo' && (
              <tr><td>Créditos PIS</td><td className="num">({fmtMoeda(apuracao.creditos_pis)})</td></tr>
            )}
            <tr><td>PIS apurado</td><td className="num">{fmtMoeda(apuracao.pis_apurado)}</td></tr>
            <tr><td>Retenção R-4020</td><td className="num">({fmtMoeda(apuracao.pis_retencao)})</td></tr>
            {Number(apuracao.pis_saldo_credor) > 0 && (
              <tr><td>Saldo credor (próx. mês)</td>
                  <td className="num" style={{ color: 'var(--success)' }}>{fmtMoeda(apuracao.pis_saldo_credor)}</td></tr>
            )}
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold">PIS a pagar</td>
              <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(apuracao.pis_a_pagar)}</td>
            </tr>
          </tbody>
        </table>

        <h3 style={{ fontSize: 12, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 16, marginBottom: 8 }}>
          COFINS (DARF {apuracao.cofins_darf_codigo})
        </h3>
        <table className="data" style={{ fontSize: 12 }}>
          <tbody>
            <tr><td>Alíquota</td><td className="num">{(Number(apuracao.cofins_aliquota) * 100).toFixed(2)}%</td></tr>
            {apuracao.regime === 'nao_cumulativo' && (
              <tr><td>Créditos COFINS</td><td className="num">({fmtMoeda(apuracao.creditos_cofins)})</td></tr>
            )}
            <tr><td>COFINS apurado</td><td className="num">{fmtMoeda(apuracao.cofins_apurado)}</td></tr>
            <tr><td>Retenção R-4020</td><td className="num">({fmtMoeda(apuracao.cofins_retencao)})</td></tr>
            {Number(apuracao.cofins_saldo_credor) > 0 && (
              <tr><td>Saldo credor (próx. mês)</td>
                  <td className="num" style={{ color: 'var(--success)' }}>{fmtMoeda(apuracao.cofins_saldo_credor)}</td></tr>
            )}
            <tr style={{ background: 'var(--gold-pale)' }}>
              <td className="bold">COFINS a pagar</td>
              <td className="num bold" style={{ color: 'var(--gold)' }}>{fmtMoeda(apuracao.cofins_a_pagar)}</td>
            </tr>
          </tbody>
        </table>

        <div className="flex" style={{ marginTop: 20, gap: 12, justifyContent: 'space-between' }}>
          <div className="small muted">
            Vencimento dia 25 → <strong>{fmtData(apuracao.darf_data_vencto)}</strong>
          </div>
          <button className="primary" disabled title="Geração de DARF — próxima sprint">
            Gerar DARFs (em breve)
          </button>
        </div>
      </div>
    </div>
  );
}
